import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  useTheme,
} from '@mui/material'
import { ChangeEvent, useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { exportReferralsByDateFilter } from '../../services/referralService'
import useCustomStyles from '../../utils/hooks/CustomStylesHook'
import {
  getErrorsAfterStartDateChange,
  getFiltersAfterModeChange,
  getFiltersAfterStartDateChange,
  getPresetDateRange,
  toDateInput,
} from './referralExportUtils'

import {
  EXPORT_BETWEEN_DATES_LABEL,
  EXPORT_BUTTON_LABEL,
  EXPORT_DATE_LABEL,
  EXPORT_DATE_RANGE_EXCEEDED,
  EXPORT_END_DATE_BEFORE_START,
  EXPORT_END_DATE_IN_FUTURE,
  EXPORT_END_DATE_LABEL,
  EXPORT_END_DATE_REQUIRED,
  EXPORT_FILTER_MODE_BETWEEN_DATES,
  EXPORT_FILTER_MODE_GREATER_THAN_OR_EQUAL,
  EXPORT_GREATER_THAN_OR_EQUAL_LABEL,
  EXPORT_LOCATION_OPTIONAL_LABEL,
  EXPORT_MAX_RANGE_DAYS,
  EXPORT_MS_PER_DAY,
  EXPORT_ONE_MONTH_DAYS,
  EXPORT_PRESET_ONE_MONTH_LABEL,
  EXPORT_PRESET_THREE_MONTHS_LABEL,
  EXPORT_REFERRAL_DIALOG_TITLE,
  EXPORT_START_DATE_IN_FUTURE,
  EXPORT_START_DATE_LABEL,
  EXPORT_START_DATE_REQUIRED,
  EXPORT_THREE_MONTHS_DAYS,
  EXPORTING_BUTTON_LABEL,
  TOAST_EXPORT_REFERRAL_FAILED,
  TOAST_EXPORT_REFERRAL_SUCCESS,
} from './constants'

type ExportFilterMode =
  | typeof EXPORT_FILTER_MODE_BETWEEN_DATES
  | typeof EXPORT_FILTER_MODE_GREATER_THAN_OR_EQUAL

type ExportFilters = {
  mode: ExportFilterMode
  startDate: string
  endDate: string
  location: string | null
}

type ExportErrors = {
  startDate?: string
  endDate?: string
}

export type ReferralExportDialogProps = {
  open: boolean
  onClose: () => void
  locationOptions: string[]
}

const initialExportFilters: ExportFilters = {
  mode: EXPORT_FILTER_MODE_BETWEEN_DATES,
  startDate: '',
  endDate: '',
  location: null,
}

const diffInDays = (fromDate: string, toDate: string) => {
  const from = new Date(`${fromDate}T00:00:00`).getTime()
  const to = new Date(`${toDate}T00:00:00`).getTime()
  return Math.floor((to - from) / EXPORT_MS_PER_DAY)
}

const styles = () => ({
  dateFieldsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '16px',
  },
})

const ReferralExportDialog = ({
  open,
  onClose,
  locationOptions,
}: ReferralExportDialogProps) => {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  const [isExporting, setIsExporting] = useState(false)
  const [exportFilters, setExportFilters] =
    useState<ExportFilters>(initialExportFilters)
  const [exportErrors, setExportErrors] = useState<ExportErrors>({})

  const resetExportDialogState = useCallback(() => {
    setIsExporting(false)
    setExportFilters(initialExportFilters)
    setExportErrors({})
  }, [])

  useEffect(() => {
    if (!open) {
      resetExportDialogState()
    }
  }, [open, resetExportDialogState])

  const validateExportFilters = () => {
    const errors: ExportErrors = {}
    const today = toDateInput(new Date())
    const { mode, startDate, endDate } = exportFilters

    if (!startDate) {
      errors.startDate = EXPORT_START_DATE_REQUIRED
    }

    if (startDate && startDate > today) {
      errors.startDate = EXPORT_START_DATE_IN_FUTURE
    }

    switch (mode) {
      case EXPORT_FILTER_MODE_BETWEEN_DATES:
        if (!endDate) {
          errors.endDate = EXPORT_END_DATE_REQUIRED
          break
        }

        if (endDate > today) {
          errors.endDate = EXPORT_END_DATE_IN_FUTURE
          break
        }

        if (startDate && endDate < startDate) {
          errors.endDate = EXPORT_END_DATE_BEFORE_START
          break
        }

        if (
          startDate &&
          diffInDays(startDate, endDate) > EXPORT_MAX_RANGE_DAYS
        ) {
          errors.endDate = EXPORT_DATE_RANGE_EXCEEDED
        }
        break

      case EXPORT_FILTER_MODE_GREATER_THAN_OR_EQUAL:
      default:
        break
    }

    setExportErrors(errors)
    return Object.keys(errors)?.length === 0
  }

  const handleExport = async () => {
    if (!validateExportFilters()) return

    setIsExporting(true)
    try {
      await exportReferralsByDateFilter(
        exportFilters?.startDate,
        exportFilters?.endDate || undefined,
        exportFilters?.location
      )

      toast.success(TOAST_EXPORT_REFERRAL_SUCCESS)
      onClose()
    } catch (error) {
      toast.error(TOAST_EXPORT_REFERRAL_FAILED)
      console.error('Export error:', error)
    } finally {
      setIsExporting(false)
    }
  }

  const handleOneMonthPreset = useCallback(() => {
    const { startDate, endDate } = getPresetDateRange(EXPORT_ONE_MONTH_DAYS)
    setExportFilters((prev) => ({
      ...prev,
      mode: EXPORT_FILTER_MODE_BETWEEN_DATES,
      startDate,
      endDate,
    }))
    setExportErrors({})
  }, [])

  const handleThreeMonthsPreset = useCallback(() => {
    const { startDate, endDate } = getPresetDateRange(EXPORT_THREE_MONTHS_DAYS)
    setExportFilters((prev) => ({
      ...prev,
      mode: EXPORT_FILTER_MODE_BETWEEN_DATES,
      startDate,
      endDate,
    }))
    setExportErrors({})
  }, [])

  const handleModeChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const mode = e.target.value as ExportFilterMode
    setExportFilters((prev) => getFiltersAfterModeChange(prev, mode))
    setExportErrors({})
  }, [])

  const handleStartDateChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const startDate = e.target.value
      setExportFilters((prev) =>
        getFiltersAfterStartDateChange(prev, startDate)
      )
      setExportErrors((prev) => getErrorsAfterStartDateChange(prev))
    },
    []
  )

  const handleEndDateChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const endDate = e.target.value
      setExportFilters((prev) => ({
        ...prev,
        endDate,
      }))
      setExportErrors((prev) => ({
        ...prev,
        endDate: undefined,
      }))
    },
    []
  )

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
      <DialogTitle>{EXPORT_REFERRAL_DIALOG_TITLE}</DialogTitle>
      <DialogContent dividers>
        <Box display={'flex'} flexDirection={'column'} gap={2} pt={1}>
          <Box display={'flex'} gap={1}>
            <Button
              variant='outlined'
              size='small'
              onClick={handleOneMonthPreset}
            >
              {EXPORT_PRESET_ONE_MONTH_LABEL}
            </Button>
            <Button
              variant='outlined'
              size='small'
              onClick={handleThreeMonthsPreset}
            >
              {EXPORT_PRESET_THREE_MONTHS_LABEL}
            </Button>
          </Box>
          <FormControl>
            <RadioGroup
              row
              value={exportFilters?.mode}
              onChange={handleModeChange}
            >
              <FormControlLabel
                value={EXPORT_FILTER_MODE_BETWEEN_DATES}
                control={<Radio size='small' />}
                label={EXPORT_BETWEEN_DATES_LABEL}
              />
              <FormControlLabel
                value={EXPORT_FILTER_MODE_GREATER_THAN_OR_EQUAL}
                control={<Radio size='small' />}
                label={EXPORT_GREATER_THAN_OR_EQUAL_LABEL}
              />
            </RadioGroup>
          </FormControl>
          <Box className={classes?.dateFieldsGrid}>
            <TextField
              label={
                exportFilters?.mode === EXPORT_FILTER_MODE_GREATER_THAN_OR_EQUAL
                  ? EXPORT_DATE_LABEL
                  : EXPORT_START_DATE_LABEL
              }
              type='date'
              size='small'
              value={exportFilters?.startDate}
              inputProps={{ max: toDateInput(new Date()) }}
              onChange={handleStartDateChange}
              InputLabelProps={{ shrink: true }}
              error={Boolean(exportErrors?.startDate)}
              helperText={exportErrors?.startDate}
            />
            {exportFilters?.mode === EXPORT_FILTER_MODE_BETWEEN_DATES && (
              <TextField
                label={EXPORT_END_DATE_LABEL}
                type='date'
                size='small'
                value={exportFilters?.endDate}
                inputProps={{
                  max: toDateInput(new Date()),
                  min: exportFilters?.startDate || undefined,
                }}
                onChange={handleEndDateChange}
                InputLabelProps={{ shrink: true }}
                error={Boolean(exportErrors?.endDate)}
                helperText={exportErrors?.endDate}
              />
            )}
          </Box>
          <Autocomplete
            options={locationOptions}
            value={exportFilters?.location}
            onChange={(_, value) =>
              setExportFilters((prev) => ({ ...prev, location: value }))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={EXPORT_LOCATION_OPTIONAL_LABEL}
                size='small'
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant='contained'
          onClick={handleExport}
          disabled={isExporting}
        >
          {isExporting ? EXPORTING_BUTTON_LABEL : EXPORT_BUTTON_LABEL}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ReferralExportDialog
